import { HelpLang, LocalText, pickText } from './types';
import { currentHelpLang } from './lang';

/**
 * Every fixed word help mode itself shows (card labels, the help bar, "Why can't I?" reasons).
 * Add a `bn` next to `en` to translate one; `{name}` marks a value filled in at run time.
 */
const UI_TEXT = {
    whatItDoes: { en: 'What it does' },
    businessMeaning: { en: 'Business meaning' },
    whatHappensNext: { en: 'What happens next' },
    whatThisAreaIsFor: { en: 'What this area is for' },
    howTheyConnect: { en: 'How they connect' },
    featuresHere: { en: 'Features here' },
    thisArea: { en: 'This area' },
    learnMore: { en: 'Learn more' },
    explainSection: { en: 'Explain this section' },
    unavailable: { en: 'unavailable: {why}' },
    whoCanUse: { en: 'Who can use it' },
    needsAllPermissions: { en: 'It needs all of these permissions together: {keys}. ' },
    roles: { en: 'Roles: {roles}. ' },
    youCan: { en: 'You can.' },
    youCantAskRole: { en: "You can't. Ask someone with the role: {role}." },
    youCantAskAdmin: { en: "You can't. Ask your administrator." },
    whyCantI: { en: "Why can't I?" },
    closeCard: { en: 'Close help card' },
    loadFailed: { en: 'Help could not be loaded. Try again in a moment.' },
    notWritten: { en: "Help for this part isn't written yet. We've noted it so it gets added." },
    barTouch: { en: 'Help mode: tap anything to learn about it.' },
    barPointer: { en: 'Help mode: drag the ? onto something, drag a box over an area, or click a section.' },
    touchMode: { en: 'Touch mode' },
    leaveEsc: { en: 'Leave help mode (Esc)' },
    leave: { en: 'Leave help mode' },
    open: { en: 'Open help mode' },
    openTooltip: { en: 'Help: explain anything on this page' },
    whyUnknown: { en: 'This is unavailable right now. This screen has not said why yet.' },
    whyMissingKey: { en: "You can't do this because your role doesn't include the permission for it." },
    whyAskRoles: { en: 'Ask someone with the role: {roles}.' },
    whyAskAdmin: { en: 'Ask your administrator.' },
    whyModuleOff: { en: 'The {module} module is switched off for your company. Ask your administrator to turn it on.' },
    whyThisModuleOff: { en: 'This module is switched off for your company. Ask your administrator to turn it on.' },
    whyPlanLimit: { en: "Your plan's limit has been reached. Ask your administrator about upgrading." },
    whyDefault: { en: 'This is unavailable right now.' },
    shortUnknown: { en: 'not available right now' },
    shortMissingKey: { en: "your role doesn't include it" },
    shortModuleOff: { en: 'module is switched off' },
    shortPlanLimit: { en: 'plan limit reached' },
} satisfies Record<string, LocalText>;

export type UiTextKey = keyof typeof UI_TEXT;

/** A help-mode label in the current help language, with `{name}` values filled in. */
export function uiText(key: UiTextKey, values: Record<string, string> = {}, lang: HelpLang = currentHelpLang()): string {
    return pickText(UI_TEXT[key], lang).replace(/\{(\w+)\}/g, (m, name: string) => values[name] ?? m);
}
